import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { createAction, props, on, createSelector } from '@ngrx/store';
import { EntityStoreFactory, IGenericEntityState } from '@pit/core-store';
import { IDtoResource } from '@pit/frontend-model';
import { EntityDataServiceAccessorService } from '@pit/core-data';
import { IDtoResourceEntityDataService } from '../../module.data/services/interfaces/dto.resource.entity.data.service.interface';

export class DtoResourceStoreFactory extends EntityStoreFactory<IDtoResource> {

    private static _instance: DtoResourceStoreFactory = null;

    public static get instance(): DtoResourceStoreFactory {
        if (this._instance === null) {
            this._instance = new DtoResourceStoreFactory();
        }

        return this._instance;
    }

    constructor() {
        super('DtoResource', 'dto.resource');
    }

    protected override doCreateActions(): void {
        super.doCreateActions();

        this.entityActions.add('get.by.depot', createAction(
            'Get Resources By Depot',
            props<{ mandator: string, numberDepot: string }>()
        ));
        this.entityActions.add('get.by.depot.success', createAction(
            'Get Resources By Depot Success',
            props<{ items: IDtoResource[] }>()
        ));
        this.entityActions.add('get.by.depot.failure', createAction(
            'Get Resources By Depot Failure',
            props<{ error: any }>()
        ));
    }

    protected override doCreateEffects(): void {
        super.doCreateEffects();

        this.entityEffects.add('get.by.depot', {
            effect: (actions$: Actions, dataServiceAccessor: EntityDataServiceAccessorService) => {
                return createEffect(() =>
                    actions$.pipe(
                        ofType(this.getAction('get.by.depot')),
                        map(action => action),
                        switchMap(action =>
                            (dataServiceAccessor.getEntityDataService<IDtoResource>(this.dataAccessName) as IDtoResourceEntityDataService)
                                .getResourcesForMandatorByDepotNumber$(action.mandator, action.numberDepot).pipe(
                                    map(items => this.getAction('get.by.depot.success')({ items: items })),
                                    catchError(error => of(this.getAction('get.by.depot.failure')({ error: error })))
                                )
                        )
                    )
                );
            }
        });
    }

    protected override doCreateSelectors() {
        super.doCreateSelectors();

        this.entitySelectors.add('get.resources', createSelector(
            this.getSelector('get.all.entities'),
            (items: IDtoResource[]) => items.sort((x, y) => x.resourceId.localeCompare(y.resourceId))
        ));
    }

    protected override doGetReducers<S extends IGenericEntityState<IDtoResource>>(): any {
        const result: any = super.doGetReducers();

        result.push(
            on(
                this.getAction('get.by.depot'),
                (state: any, { showLoadingIndicator }) => {
                    return {
                        ...state,
                        loading: ((showLoadingIndicator === true) ? true : state.loading)
                    };
                }
            )
        );
        result.push(
            on(
                this.getAction('get.by.depot.failure'),
                (state: any) => {
                    return {
                        ...state,
                        loading: ((state.loading === true) ? false : state.loading)
                    };
                }
            )
        );
        result.push(
            on(this.getAction('get.by.depot.success'), (state: any, { items }) => {
                const res = this.entityAdapter.setAll(items, {
                    ...state,
                    loading: false,
                    selectedItem: null
                });
                return res;
            })
        );

        return result;
    }

}
